import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";

const STATUS_TONE = {
  NotImplemented: "bg-red-50 text-red-700 border-red-200",
  Partial: "bg-amber-50 text-amber-700 border-amber-200",
  Implemented: "bg-emerald-50 text-emerald-700 border-emerald-200",
  NotApplicable: "bg-gray-100 text-gray-500 border-gray-200",
  NotAssessed: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_LABEL = {
  NotImplemented: "Not Implemented",
  Partial: "Partial",
  Implemented: "Implemented",
  NotApplicable: "Not Applicable",
  NotAssessed: "Not Assessed",
};

/**
 * Phase 4: the GRC controls that currently name this account.
 *
 * <p>The drawer could say an account was privileged, dormant and reachable, and never say that
 * those facts were failing a control someone owns. This closes that: the account is listed, the
 * control it is evidence for is named, and the obligation is stated in the framework's own terms.</p>
 *
 * <p>The status shown is the CONTROL's, computed over its whole population — not a verdict on this
 * account. An account appearing here is evidence for the control; it does not by itself fail it,
 * and the wording says so rather than leaving the reader to infer it.</p>
 */
export default function IdentityControls({ id }) {
  const apiCall = useApi();
  const [data, setData] = useState(null);   // null = loading
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null); setFailed(false);
    apiCall("GET", `/defender-identity/identities/${id}/controls`)
      .then(r => { if (!cancelled) (r && Array.isArray(r.controls) ? setData(r) : setFailed(true)); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [apiCall, id]);

  const controls = data?.state === "available" ? data.controls : [];
  const named = controls.filter(c => c.status !== "NotAssessed");
  const notAssessed = controls.filter(c => c.status === "NotAssessed");

  return (
    <>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Controls affected</div>

      {failed && (
        <div className="text-xs text-gray-500 py-1.5" data-testid="identity-controls-failed">
          The controls for this account could not be read.
        </div>
      )}
      {!failed && data === null && <div className="text-xs text-gray-400 py-1.5">Loading…</div>}

      {/* "Not computed yet" is not "no control names this account" — the three-state contract the
          rest of the drawer follows. */}
      {data?.state === "notRead" && (
        <div className="text-[11px] text-amber-700 py-1.5" data-testid="identity-controls-not-read">
          {data.message}
        </div>
      )}

      {data?.state === "available" && named.length === 0 && notAssessed.length === 0 && (
        <div className="text-xs text-gray-400 py-1.5" data-testid="identity-controls-none">
          No identity assurance control names this account.
        </div>
      )}

      {named.length > 0 && (
        <ul className="space-y-1.5 py-1" data-testid="identity-controls">
          {named.map(c => <ControlRow key={c.controlId} c={c} />)}
          <li className="text-[11px] text-gray-400 leading-relaxed">
            This account is counted in each control above. The status is the control’s own, across
            every account in its scope — not a verdict on this one.
          </li>
        </ul>
      )}

      {/* A Not Assessed control names no account, so "no control names this account" would be a
          false all-clear: whether it would name this one is unknown, and the reason says why. */}
      {notAssessed.length > 0 && (
        <ul className="space-y-1.5 py-1" data-testid="identity-controls-not-assessed">
          {notAssessed.map(c => <ControlRow key={c.controlId} c={c} />)}
          <li className="text-[11px] text-gray-400 leading-relaxed">
            Not assessed for any account yet, so whether these controls name this account is unknown.
          </li>
        </ul>
      )}
    </>
  );
}

function ControlRow({ c }) {
  return (
    <li className="text-xs rounded border border-gray-200 px-2.5 py-2" data-testid={`identity-control-${c.controlId}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-gray-900 truncate" title={c.title}>{c.title}</span>
        <span className={`px-1 rounded border flex-shrink-0 ${STATUS_TONE[c.status] || STATUS_TONE.NotAssessed}`}>
          {STATUS_LABEL[c.status] || c.status}
        </span>
      </div>
      {c.summary && <div className="text-gray-500 mt-0.5 leading-relaxed">{c.summary}</div>}
      {c.frameworks?.length > 0 && (
        <div className="text-[11px] text-gray-500 mt-1">
          Required by: {c.frameworks.join(" · ")}
        </div>
      )}
    </li>
  );
}
